
import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Upload } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getFileType } from "@/utils/fileProcessing";

export interface DropZoneProps {
  onFileSelected: (files: File[]) => void;
  disabled: boolean;
  isProcessing?: boolean;
  selectedFiles?: File[];
}

const DropZone: React.FC<DropZoneProps> = ({ 
  onFileSelected, 
  disabled, 
  isProcessing = false, 
  selectedFiles = []
}) => {
  const { toast } = useToast();
  
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles || acceptedFiles.length === 0) {
        toast({
          title: "Erro",
          description: "Nenhum arquivo selecionado.",
          variant: "destructive",
        });
        return;
      }
      
      const validFiles: File[] = [];
      const allowedTypes = ["pdf", "png", "jpg", "jpeg", "doc", "docx", "txt"];
      const maxFileSize = 20 * 1024 * 1024; // 20MB - limite otimizado para estabilidade
      const warningSize = 15 * 1024 * 1024; // 15MB - aviso de tamanho
      
      for (const file of acceptedFiles) {
        const fileType = getFileType(file);
        
        if (!fileType || !allowedTypes.includes(fileType)) {
          toast({
            title: "Tipo de Arquivo Não Suportado",
            description: `Arquivo "${file.name}" não é suportado. Use PDF, PNG, JPG, JPEG, DOC, DOCX ou TXT.`,
            variant: "destructive",
          });
          continue;
        }
        
        if (file.size > maxFileSize) {
          toast({
            title: "Arquivo Muito Grande",
            description: `Arquivo "${file.name}" deve ter menos de 20MB para garantir processamento estável. Tamanhos ideais: até 10MB.`,
            variant: "destructive",
          });
          continue;
        }
        
        // Aviso para arquivos grandes (mas dentro do limite)
        if (file.size > warningSize) {
          toast({
            title: "Arquivo Grande Detectado",
            description: `Arquivo "${file.name}" (${(file.size / 1024 / 1024).toFixed(1)}MB) pode demorar mais para processar. Ideal: até 10MB.`,
            variant: "default",
          });
        }
        
        validFiles.push(file);
      }
      
      if (validFiles.length === 0) {
        toast({
          title: "Nenhum arquivo válido",
          description: "Todos os arquivos foram rejeitados. Verifique os tipos e tamanhos permitidos.",
          variant: "destructive",
        });
        return;
      }
      
      if (validFiles.length > 0) {
        toast({
          title: "Arquivos Selecionados",
          description: `${validFiles.length} arquivo(s) válido(s) selecionado(s) para processamento.`,
          variant: "default",
        });
        onFileSelected(validFiles);
      }
    },
    [onFileSelected, toast]
  );
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop,
    disabled: disabled || isProcessing,
    multiple: true,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    }
  });

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-md p-6 cursor-pointer transition-colors duration-200
        ${
          isDragActive
            ? "bg-secondary/50 border-primary"
            : "border-muted-foreground/50 bg-secondary"
        }
        ${disabled || isProcessing ? "cursor-not-allowed opacity-50" : ""}`}
    >
      <input {...getInputProps()} />
      <div className="text-center">
        <Upload className="mx-auto h-6 w-6 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">
          {isDragActive
            ? "Solte os arquivos aqui..."
            : "Arraste e solte múltiplos arquivos ou clique para selecionar"}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Suporta PDF, PNG, JPG, JPEG, DOC, DOCX e TXT (máx. 20MB • ideal: até 10MB)
        </p>
        {selectedFiles.length > 0 && (
          <p className="mt-1 text-xs text-primary font-medium">
            {selectedFiles.length} arquivo(s) selecionado(s)
          </p>
        )}
      </div>
    </div>
  );
};

export default DropZone;
